import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Box, Typography } from '@material-ui/core';

const AI_PATH = (process.env.REACT_APP_AI_PATH || '').trim();
const POLL_MS = 30 * 1000;

const useStyles = makeStyles(() => ({
    bar: {
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        padding: '6px 16px',
        fontWeight: 600,
        textAlign: 'center',
        cursor: 'pointer',
        borderTop: '1px solid rgba(0,0,0,0.06)'
    },
    marquee: {
        display: 'inline-block',
        paddingLeft: '100%',
        animation: '$scroll 22s linear infinite'
    },
    '@keyframes scroll': {
        '0%':   { transform: 'translateX(0)' },
        '100%': { transform: 'translateX(-100%)' }
    },
    '@keyframes blink': {
        '0%, 100%': { opacity: 1 },
        '50%':      { opacity: 0.55 }
    },
    blink: { animation: '$blink 1.1s ease-in-out infinite' }
}));

const PendingWarningBar = () => {
    const classes = useStyles();
    const isWaAgent = React.useMemo(() => {
        try { return localStorage.getItem('whatsappAgent') === 'true'; } catch (e) { return false; }
    }, []);
    const agentId = React.useMemo(() => {
        try { return localStorage.getItem('adminId') || null; } catch (e) { return null; }
    }, []);
    const [state, setState] = React.useState({ count: 0, oldestMinutes: 0, oldest: null });

    React.useEffect(() => {
        if (!isWaAgent || !agentId || !AI_PATH) return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                const res = await axios.get(`${AI_PATH}/api/ai/whatsapp/pending-touch-count`, {
                    params: { agentId }
                });
                if (!cancelled && res.data?.success) {
                    setState({
                        count:         res.data.count || 0,
                        oldestMinutes: res.data.oldestMinutes || 0,
                        oldest:        res.data.oldest || null
                    });
                }
            } catch (e) { /* silent */ }
        };
        load();
        const iv = setInterval(load, POLL_MS);
        return () => { cancelled = true; clearInterval(iv); };
    }, [isWaAgent, agentId]);

    if (!isWaAgent) return null;
    const { count, oldestMinutes, oldest } = state;
    if (count === 0 || oldestMinutes < 5) return null;

    // TAT window is 60 min. `tatLeft` is the countdown shown on every tier.
    const tatLeft = Math.max(0, 60 - oldestMinutes);
    // Tier by oldest age
    let bg, fg, mode, msg;
    const who = oldest ? `${oldest.customerName || oldest.phone}${oldest.phone && oldest.customerName ? ` (${oldest.phone})` : ''}` : '';
    if (oldestMinutes < 15) {
        bg = '#FFF3CD'; fg = '#856404'; mode = 'static';
        msg = `⚠️  ${count} lead${count > 1 ? 's' : ''} unreplied — oldest ${oldestMinutes} min · TAT ${tatLeft}m left. Reach out now.`;
    } else if (oldestMinutes < 45) {
        bg = '#FFE0B2'; fg = '#8B4A00'; mode = 'marquee';
        msg = `⏰  ${count} lead${count > 1 ? 's' : ''} pending • Oldest ${oldestMinutes} min — ${who} • TAT ${tatLeft}m left before auto-shift • Send a WA reply now to stop the timer`;
    } else {
        bg = '#F8D7DA'; fg = '#721C24'; mode = 'blink';
        msg = `🚨 URGENT — ${who} will auto-shift to another agent in ${Math.max(1, tatLeft)} min (TAT). Reply now to save the lead. (${count} pending)`;
    }

    return (
        <Box
            component={Link}
            to="/dashboard/my-chats"
            className={`${classes.bar} ${mode === 'blink' ? classes.blink : ''}`}
            style={{ background: bg, color: fg, textDecoration: 'none', display: 'block' }}
        >
            {mode === 'marquee' ? (
                <Typography component="span" variant="body2" className={classes.marquee}>
                    {msg}
                </Typography>
            ) : (
                <Typography component="span" variant="body2">{msg}</Typography>
            )}
        </Box>
    );
};

export default PendingWarningBar;
